import Constants from '../../app/constants.js'

const defaultFolderSettings = { items_order: 'modified_ts', items_order_descending: 1 };

export const buildObjectsTree = (objects, opts, folderSettings) => {
  const templateIdCounts = {};
  const superfolderIdCounts = {};

  const enableSharedToMeFolder = opts.shared_to_me_enabled !== undefined ? opts.shared_to_me_enabled : true

  let shared = {
    id: Constants.SHARED_TO_ME_FOLDER_ID,
    parent_id: 0,
    company_id: 0,
    title: { id: 'folder.sharedToMe' },
    type: 'folder',
    right: 'view',
    modified_ts: 0,
    objects: {}
  };
  let sharedObjectIdsInRoot = [];

  objects[0] = {
    id: 0,
    company_id: opts.company_id,
    title: '',
    right: 'edit',
    objects: {},
    type: 'folder'
  };

  for (const key in objects) {
    if (folderSettings && folderSettings[key]) {
      objects[key].folderSettings = folderSettings[key];
    } else {
      objects[key].folderSettings = defaultFolderSettings;
      if (objects[key].items_order) {
        // Object has "general" items_order. It's possibly copied from superfolder.
        objects[key].folderSettings = { ...folderSettings, items_order: objects[key].items_order };
      }
    }

    if (key === '0') {
      continue;
    }

    if (Object.prototype.hasOwnProperty.call(objects, key)) {
      const object = objects[key];

      if (object.sub_canceled) {
        object.right = 'view';
      }

      // default to owner right, because right does not come with non-shared objects
      if (!object.right) object.right = 'owner';

      if (object.template_id) {
        if (!templateIdCounts[object.template_id]) {
          templateIdCounts[object.template_id] = 0;
        }
        templateIdCounts[object.template_id]++;
      }

      if (object.superfoldertype_id) {
        if (!superfolderIdCounts[object.superfoldertype_id]) {
          superfolderIdCounts[object.superfoldertype_id] = 0;
        }
        superfolderIdCounts[object.superfoldertype_id]++;
      }

      if (!objects[object.parent_id] && object.in_superfolder_id) {
        object.parent_id = object.in_superfolder_id;
      }

      // If there's a parent folder, put this object to parent.objects with same key, otherwise put in root
      if (object.parent_id && objects[object.parent_id]) {
        const parent = objects[object.parent_id];

        if (typeof parent.objects === 'undefined') {
          parent.objects = {};
        }

        parent.objects[key] = object;

        // If object is from users own company, but its parent is not,
        // put the object also in company root
        if (object.company_id == opts.company_id && parent.company_id != opts.company_id) {
          objects[0].objects[key] = object;
        }

      } else {
        if (!enableSharedToMeFolder || object.company_id == opts.company_id) {
          // The object is in users company
          objects[0].objects[key] = object;
        } else {
          // This would be in root, but the new way is to put it inside the 'shared to me' folder
          sharedObjectIdsInRoot.push(object.id);
        }
      }
    }
  }

  if (enableSharedToMeFolder) {
    // These 'virtual folders' (shared to me with the company level)
    //   needs to be processed in phases to make ts-bubbling work properly.
    //   (One can't bubble something into something which does not exist yet.)

    // Generate 'shared to me' company level folder structure after iterating and ts-bubbling every other object.
    for (let sharedObjectId of sharedObjectIdsInRoot) {
      const object = objects[sharedObjectId];
      const company_key = 0 - object.company_id; // Utilize negative numbers for shared from company_id's
      if (!shared.objects[company_key]) {
        // Create the company level
        shared.objects[company_key] = {
          id: company_key,
          parent_id: Constants.SHARED_TO_ME_FOLDER_ID, // Company level parent is the 'shared to me' folder.
          company_id: object.company_id,
          title: object.company_name,
          owner_name: object.owner_name,
          type: 'folder',
          right: 'view',
          modified_ts: 0,
          objects: {}
        }
      }
      object.parent_id = company_key; // Set 'correct' parent
      shared.objects[company_key].objects[sharedObjectId] = object; // Add object to the 'correct' shared company folder
    }

    // 'Point' shared objects to the data structure ('shared' is a pointer)
    objects[Constants.SHARED_TO_ME_FOLDER_ID] = shared; // root level (every object is accessible from root)
    objects[0].objects[Constants.SHARED_TO_ME_FOLDER_ID] = shared; // parent (0) level link

    /* We can't set folderSettings for sharedToMe-folder, because its id is "shared". 
     * It should be number. Or we should edit folder_settings table and logic.

    if (folderSettings && folderSettings[Constants.SHARED_TO_ME_FOLDER_ID]) {
      objects[Constants.SHARED_TO_ME_FOLDER_ID].folderSettings = folderSettings[Constants.SHARED_TO_ME_FOLDER_ID];
    } else {
      objects[Constants.SHARED_TO_ME_FOLDER_ID].folderSettings = defaultFolderSettings;
    }
    */

    // 'Point' shared company levels to root
    for (let company_key in shared.objects) {
      console.log("company_key = "+company_key)
      objects[company_key] = shared.objects[company_key];
    }

    // Remove "shared to me" folder from root, but keep other data untouched
    delete objects[0].objects[Constants.SHARED_TO_ME_FOLDER_ID];
  }

  return {templateIdCounts, superfolderIdCounts};
}

/** Action requirements **
        Moving:
          owner-right
        Deleting:
          owner-right
          OR
          manage right to the object and its parent
        Renaming:
          owner-right
          OR
          manage right to the object and its parent
        Sharing:
          owner-right
          OR
          manage right to the object
        Copying:
          ability to create documents (canAddDocument) AND
          document type of object AND
            owner/edit rights at the same company
            OR
            manage right to the object and its parent
*/
export const evaluateRights = (object, parent, userCompanyId, userCompanyFeatures, folderCompanyFeatures) => {
  const rights = {
    move: false,
    delete: false,
    copy: false,
    rename: false,
    share: false
  };

  if (!object) {
    return rights;
  }

  let canAddDocument = false;

  if (parent?.company_id === userCompanyId) {
    canAddDocument = !!userCompanyFeatures?.documents;
  } else if (parent?.right === 'manage') {
    canAddDocument = !!folderCompanyFeatures?.documents;
  }

  if (object.right === 'owner') {
    rights.move = true;
  }

  if (object.right === 'manage' &&
    userCompanyId === object.company_id
  ) {
    rights.move = true;
  }

  if (
    object.right === 'owner' ||
    object.right === 'manage'
  ) {
    rights.share = true;
  }

  if (
    object.right === 'owner' ||
    (object.right === 'manage' && (parent?.right === 'owner' || parent?.right === 'manage'))
  ) {
    rights.delete = true;
    rights.rename = true;
  }

  if (
    canAddDocument &&
    object.type === 'document' &&
    (
      (
        (object.right === 'owner' || object.right === 'edit') &&
        userCompanyId === object.company_id
      )
      ||
      (
        object.right === 'manage' && parent?.right === 'manage'
      )
    )
  ) {
    rights.copy = true;
  }

  if (object.type === 'compilationsfolder') {
    rights.move = false;
    rights.delete = false;
    rights.rename = false;
    rights.copy = false;
  }

  return rights;
}

export const buildObjectPath = (objects, object) => {
  var path = [];
  if (!object) {
    return path;
  }

  /**
   * object.parent_id = object.in_superfolder_id; is done in buildObjectTree(), don't do it here.
   * This is not the place to modify objectTree, this is meant to get objectPath.
   * object is read-only at least when this function is called from ObjectBreadcrumb, then object is props.object
   * Seems that there is not easy way to check if object is read-only.
   */

  let parentId = object.parent_id;
  if (!objects[object.parent_id] && object.in_superfolder_id) {
    parentId = object.in_superfolder_id;
  }

  if (parentId !== 0) {
    if (objects[parentId]) {
      var parent = objects[parentId];
      path = buildObjectPath(objects, parent);
    } else {
      // parent not shared to this user, skip
    }
  }
  path.push({
    title: object.title,
    id: object.id,
    isSuperfolder: object.superfoldertype_id ? true : false,
    propagateViewOnShare: object.propagate_view_on_share ? true : false,
    type: object.type,
    right: object.right,
  });

  return path;
}

export const COPY_OPTIONS_SETTING = 'copy_document_options';

export const defaultCopyOptions = {
  copyImages: true,
  copyFiles: true,
  copySignatures: true,
};

export const parseCopyOptions = (settingValue) => {
  if (!settingValue || typeof settingValue !== 'string') {
    return defaultCopyOptions;
  }
  try {
    const parsed = JSON.parse(settingValue);
    return {
      copyImages: parsed.copyImages !== false,
      copyFiles: parsed.copyFiles !== false,
      copySignatures: parsed.copySignatures !== false,
    };
  } catch {
    return defaultCopyOptions;
  }
};
